import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, Image as ImageIcon, Video as VideoIcon, Trash2, RefreshCw } from "lucide-react";

/**
 * Uploads library for the Graphic Designer (dark-themed).
 * Props:
 *   kind         "image" | "video"
 *   onPick(url)  Called with the storage URL when the user selects an asset.
 */
export default function AssetsLibrary({ kind = "image", onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const isVideo = kind === "video";
  const accept = isVideo ? "video/mp4,video/webm,video/quicktime" : "image/*";
  const endpoint = isVideo ? "/upload/video" : "/upload";
  const maxLabel = isVideo ? "25 MB" : "5 MB";

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/assets?kind=${kind}&limit=60`);
      setItems(data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load uploads");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (isVideo && !file.type.startsWith("video/")) return toast.error("Please pick a video file");
    if (!isVideo && !file.type.startsWith("image/")) return toast.error("Please pick an image file");
    setUploading(true); setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success(`${isVideo ? "Video" : "Image"} uploaded`);
      // prepend the freshly-uploaded item
      setItems((prev) => [{ ...data }, ...prev]);
      // Auto-place onto canvas
      if (onPick) onPick(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); setProgress(0); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this upload?")) return;
    try {
      await api.delete(`/assets/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-4 space-y-3" data-testid={`assets-library-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-white/50 font-bold">
          {isVideo ? "Videos" : "Images"}
        </div>
        <button onClick={load} title="Refresh" className="p-1.5 rounded hover:bg-white/10">
          <RefreshCw className={"w-3.5 h-3.5 text-white/60 " + (loading ? "animate-spin" : "")} />
        </button>
      </div>

      <button
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="w-full h-20 rounded-xl border-2 border-dashed border-white/20 hover:border-[#22D3EE] flex flex-col items-center justify-center gap-1 text-xs text-white/60 disabled:opacity-50"
        data-testid={`upload-${kind}-btn`}
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Uploading… {progress}%</span>
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            <span>Upload {isVideo ? "video" : "image"}</span>
            <span className="text-[10px] text-white/40">max {maxLabel}</span>
          </>
        )}
      </button>
      {uploading && (
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-[#22D3EE] transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      <input ref={inputRef} type="file" hidden accept={accept} onChange={onFile}
             data-testid={`upload-${kind}-file`} />

      {error && <div className="text-xs text-red-400">{error}</div>}

      {items.length === 0 && !loading && (
        <div className="text-xs text-white/50 pt-1">No uploads yet. Your uploaded {isVideo ? "videos" : "images"} appear here.</div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {items.map((it) => (
          <div key={it.id} className="relative group aspect-square rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#22D3EE] overflow-hidden">
            <button
              type="button"
              onClick={() => onPick && onPick(it.url)}
              className="absolute inset-0 w-full h-full"
              data-testid={`asset-${it.id}`}
              title={it.original_filename || ""}
            >
              {isVideo ? (
                <div className="w-full h-full flex items-center justify-center bg-black/40">
                  <VideoIcon className="w-6 h-6 text-white/70" />
                </div>
              ) : (
                <img src={it.url} alt="" className="w-full h-full object-cover" loading="lazy" />
              )}
            </button>
            <button
              type="button"
              onClick={(ev) => remove(it.id, ev)}
              className="absolute top-1 right-1 z-10 p-1 rounded bg-black/60 hover:bg-red-500/80 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              data-testid={`asset-del-${it.id}`}
              aria-label="Delete asset"
            >
              <Trash2 className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
